import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Save, Loader2, X } from "lucide-react";
import { formatFullCurrency, STATUS_SELECT_OPTIONS } from "./projectUtils";

export function ProjectEditDialog({
  open, onOpenChange, selectedProject, form, setForm, balances, onSave, loading,
}) {
  const uniqueManagers = [...new Set(balances.map(b => b.project_manager).filter(Boolean))].sort();

  const handleMoneyChange = (key) => (e) => {
    const val = parseFloat(e.target.value) || 0;
    const subsidy = key === "subsidy" ? val : (form.subsidy || 0);
    const financial_savings = key === "financial_savings" ? val : (form.financial_savings || 0);
    const tax_deduction = key === "tax_deduction" ? val : (form.tax_deduction || 0);
    const expenses = key === "expenses" ? val : (form.expenses || 0);
    const income = subsidy + financial_savings + tax_deduction;
    setForm({ ...form, [key]: val, income, balance: income - expenses });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Editar Proyecto: {selectedProject?.project_name}</DialogTitle>
          <DialogDescription>Modifica los datos del proyecto I+D</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Estado</Label>
              <Select value={form.status || ""} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="edit-status"><SelectValue placeholder="Seleccionar estado" /></SelectTrigger>
                <SelectContent>
                  {STATUS_SELECT_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Responsable del proyecto</Label>
              <Input
                value={form.project_manager || ""}
                onChange={(e) => setForm({ ...form, project_manager: e.target.value })}
                placeholder="Ej: Ana García"
                list="edit-managers-list"
                data-testid="edit-project-manager"
              />
              <datalist id="edit-managers-list">
                {uniqueManagers.map(m => <option key={m} value={m} />)}
              </datalist>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Año inicio</Label>
              <Input type="number" value={form.year_start || ""}
                onChange={(e) => setForm({ ...form, year_start: e.target.value })}
                placeholder="2024" data-testid="edit-year-start" />
            </div>
            <div className="space-y-2">
              <Label>Año fin</Label>
              <Input type="number" value={form.year_end || ""}
                onChange={(e) => setForm({ ...form, year_end: e.target.value })}
                placeholder="2026" data-testid="edit-year-end" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {[
              ["budget", "Presupuesto", "edit-budget"],
              ["financing", "Financiación", "edit-financing"],
              ["expenses", "Gasto Real", "edit-expenses"],
              ["subsidy", "Subvención", "edit-subsidy"],
              ["financial_savings", "Ahorro Financiero", "edit-financial-savings"],
              ["tax_deduction", "Deducción Fiscal", "edit-tax-deduction"],
            ].map(([key, label, testid]) => (
              <div key={key} className="space-y-2">
                <Label>{label} (€)</Label>
                <Input type="number" value={form[key]} onChange={key === "budget" || key === "financing" ? (e) => setForm({ ...form, [key]: parseFloat(e.target.value) || 0 }) : handleMoneyChange(key)} data-testid={testid} />
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                Ingresos (€) <span className="text-xs text-muted-foreground font-normal">— Subv. + Ahorro + Deducción</span>
              </Label>
              <div className="flex items-center h-10 px-3 rounded-md border bg-blue-50 dark:bg-blue-900/20 font-semibold text-blue-700 dark:text-blue-300">
                {formatFullCurrency(form.income || 0)}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                Balance (€) <span className="text-xs text-muted-foreground font-normal">— Ingresos - Gastos</span>
              </Label>
              <div className={`flex items-center h-10 px-3 rounded-md border font-semibold ${
                ((form.income || 0) - (form.expenses || 0)) >= 0
                  ? "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300"
                  : "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300"
              }`}>
                {formatFullCurrency((form.income || 0) - (form.expenses || 0))}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Notas</Label>
            <Textarea
              value={form.notes || ""}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              placeholder="Notas adicionales..."
              data-testid="edit-notes"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <X className="w-4 h-4 mr-2" />Cancelar
          </Button>
          <Button onClick={onSave} disabled={loading} data-testid="save-edit-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Guardar Cambios
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
